import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { msalConfig } from './utils/msalConfig'
import { getConfig } from './config/production'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.jsx'

// Sentry: initialize before anything renders so early errors are captured.
// No-op when VITE_SENTRY_DSN is absent (local dev without Sentry wired up).
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_GIT_SHA || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,      // redact all text to avoid PII leaks
        blockAllMedia: true,    // never record user-uploaded images
        maskAllInputs: true,    // redact form fields (passwords, etc.)
      }),
    ],
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.1,    // 10% of all sessions
    replaysOnErrorSampleRate: 1.0,    // 100% of sessions with errors
    beforeSend(event) {
      // Scrub tokens that might sneak into captured URLs.
      if (event.request?.url?.includes('token=')) {
        event.request.url = event.request.url.replace(/token=[^&]+/, 'token=[FILTERED]')
      }
      return event
    },
  })
}

// Cache the environment check to prevent re-reads
const IS_PRODUCTION = window.location.hostname === 'jpro.app';
const GOOGLE_CLIENT_ID = IS_PRODUCTION ? 
  '437632205205-peocgmvn9en6pqmpisfthgjutun7jli7.apps.googleusercontent.com' : 
  import.meta.env.VITE_GOOGLE_CLIENT_ID;


// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <MsalProvider instance={msalInstance}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </GoogleOAuthProvider>
    </MsalProvider>
  </StrictMode>,
)
